/** Preferred language catalog — SPA registration SSOT for locale metadata. */

type LocaleCatalogEntry = {
  endonym: string
  clientConfigLanguage: string
  withoutCldr: boolean
  /** Browser language tags that map to this locale (exact or `alias-*`). */
  browserAliases: readonly string[]
  /** "and" for joining a list when the browser has no list rules for this language. */
  listAndWord?: string
}

export const LOCALE_CATALOG = {
  en: {
    endonym: 'English',
    clientConfigLanguage: 'English',
    withoutCldr: false,
    browserAliases: [],
  },
  es: {
    endonym: 'Español',
    clientConfigLanguage: 'Spanish',
    withoutCldr: false,
    browserAliases: [],
  },
  'pt-PT': {
    endonym: 'Português (Portugal)',
    clientConfigLanguage: 'Portuguese',
    withoutCldr: false,
    browserAliases: [],
  },
  'pt-BR': {
    endonym: 'Português (Brasil)',
    clientConfigLanguage: 'Portuguese (Brazil)',
    withoutCldr: false,
    browserAliases: [],
  },
  zh: {
    endonym: '简体中文',
    clientConfigLanguage: 'Chinese',
    withoutCldr: false,
    browserAliases: [],
  },
  'zh-TW': {
    endonym: '繁體中文',
    clientConfigLanguage: 'Traditional Chinese',
    withoutCldr: false,
    browserAliases: [],
  },
  so: {
    endonym: 'Soomaali',
    clientConfigLanguage: 'Somali',
    withoutCldr: false,
    browserAliases: [],
  },
  ht: {
    endonym: 'Kreyòl ayisyen',
    clientConfigLanguage: 'Haitian Creole',
    withoutCldr: false,
    browserAliases: [],
    listAndWord: 'ak',
  },
  mww: {
    endonym: 'Hmoob Dawb',
    clientConfigLanguage: 'White Hmong',
    withoutCldr: true,
    browserAliases: ['hmn'],
    listAndWord: 'thiab',
  },
} as const satisfies Record<string, LocaleCatalogEntry>

export type SupportedLocale = keyof typeof LOCALE_CATALOG

function catalogLocales<T extends Record<string, unknown>>(catalog: T): Array<keyof T> {
  return Object.keys(catalog) as Array<keyof T>
}

export const SUPPORTED_LOCALES = catalogLocales(LOCALE_CATALOG)

export const DEFAULT_LOCALE: SupportedLocale = 'en'

/** Locales without ICU/CLDR data in the runtime; English date/number formatting is accepted. */
export const LOCALES_WITHOUT_CLDR: ReadonlySet<SupportedLocale> = new Set(
  SUPPORTED_LOCALES.filter((tag) => LOCALE_CATALOG[tag].withoutCldr)
)

/**
 * Maps browser language tags that are not exact SupportedLocale values.
 * Example: browsers report the macrolanguage `hmn`, not the White Hmong tag `mww`.
 */
export function resolveBrowserLocaleAlias(browserLang: string): SupportedLocale | null {
  const tag = browserLang.toLowerCase()
  for (const locale of SUPPORTED_LOCALES) {
    for (const alias of LOCALE_CATALOG[locale].browserAliases) {
      if (tag === alias || tag.startsWith(`${alias}-`)) return locale
    }
  }
  return null
}

export function isSupportedLocale(value: unknown): value is SupportedLocale {
  return typeof value === 'string' && (SUPPORTED_LOCALES as readonly string[]).includes(value)
}

export function listAndWordFor(locale: string): string | undefined {
  const entry: LocaleCatalogEntry | undefined = isSupportedLocale(locale) ? LOCALE_CATALOG[locale] : undefined
  return entry?.listAndWord
}

/** Locale of a client-config language name (e.g. 'Haitian Creole' → 'ht'); undefined when unknown. */
export function localeForClientConfigLanguage(language: string | undefined): SupportedLocale | undefined {
  return SUPPORTED_LOCALES.find((locale) => LOCALE_CATALOG[locale].clientConfigLanguage === language)
}

export function clientConfigLanguageForLocale(locale: string): string {
  if (isSupportedLocale(locale)) return LOCALE_CATALOG[locale].clientConfigLanguage
  const baseLocale = locale.slice(0, 2).toLowerCase()
  if (isSupportedLocale(baseLocale)) return LOCALE_CATALOG[baseLocale].clientConfigLanguage
  return LOCALE_CATALOG[DEFAULT_LOCALE].clientConfigLanguage
}
